import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Sparkles, 
  Plus, 
  BookOpen, 
  Search, 
  ChevronRight, 
  Layers, 
  Shield, 
  Zap,
  Loader2,
  Trash2,
  Edit2,
  Info,
  HelpCircle,
  FileText,
  Scale,
  CheckCircle2,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { useApiClient } from "@/hooks/use-api-client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface PlaybookTopicRule {
  text: string;
  source_reference?: string;
}

interface PlaybookTopic {
  id: string;
  name: string;
  description?: string;
  rules: PlaybookTopicRule[];
}

interface PlaybookSkill {
  id: string;
  name: string;
  description?: string;
  source_filename?: string;
  topics?: PlaybookTopic[];
  created_at?: string;
  updated_at?: string;
  user_id?: string;
  agent_type?: string; // Optional for future DB support
}

export default function SkillsPage() {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [skills, setSkills] = useState<PlaybookSkill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  
  // Detail Modal State
  const [selectedSkill, setSelectedSkill] = useState<PlaybookSkill | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Deletion State
  const [skillToDelete, setSkillToDelete] = useState<PlaybookSkill | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchSkills = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.get('/api/v1/contract-review/skills');
      if (res.ok) {
        // Enhance skills with detected agent type from prefix
        const enhancedSkills = (res.data?.skills || []).map((skill: any) => {
          let agentType = "contract_review";
          let cleanDescription = skill.description || "";
          
          if (cleanDescription.startsWith("[LEGAL_RESEARCH]")) {
            agentType = "legal_research";
            cleanDescription = cleanDescription.replace("[LEGAL_RESEARCH]", "").trim();
          } else if (cleanDescription.startsWith("[DOCUMENT_DRAFTING]")) {
            agentType = "document_drafting";
            cleanDescription = cleanDescription.replace("[DOCUMENT_DRAFTING]", "").trim();
          } else if (cleanDescription.startsWith("[CONTRACT_REVIEW]")) {
            agentType = "contract_review";
            cleanDescription = cleanDescription.replace("[CONTRACT_REVIEW]", "").trim();
          }
          
          return { ...skill, agent_type: agentType, display_description: cleanDescription };
        });
        setSkills(enhancedSkills);
      } else {
        toast.error("No se pudieron cargar las habilidades.");
      }
    } catch (err) {
      console.error("Error fetching skills:", err);
      toast.error("Error de conexión al cargar las habilidades.");
    } finally {
      setIsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const handleViewDetails = async (skill: PlaybookSkill) => {
    setSelectedSkill(skill);
    setIsDetailOpen(true);
    setIsLoadingDetail(true);
    
    try {
      const res = await apiClient.get(`/api/v1/contract-review/skills/${skill.id}`);
      if (res.ok) {
        // Apply same detection for details
        const skillData = res.data.skill;
        let agentType = "contract_review";
        let cleanDescription = skillData.description || "";
        
        if (cleanDescription.startsWith("[LEGAL_RESEARCH]")) {
          agentType = "legal_research";
          cleanDescription = cleanDescription.replace("[LEGAL_RESEARCH]", "").trim();
        } else if (cleanDescription.startsWith("[DOCUMENT_DRAFTING]")) {
          agentType = "document_drafting";
          cleanDescription = cleanDescription.replace("[DOCUMENT_DRAFTING]", "").trim();
        }
        
        setSelectedSkill({ ...skillData, agent_type: agentType, display_description: cleanDescription });
      }
    } catch (err) {
      console.error("Error fetching skill details:", err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleDeleteSkill = async () => {
    if (!skillToDelete) return;
    
    setIsDeleting(true);
    try {
      const res = await apiClient.delete(`/api/v1/contract-review/skills/${skillToDelete.id}`);
      if (res.ok) {
        toast.success("Skill eliminado correctamente.");
        setSkills(skills.filter(s => s.id !== skillToDelete.id));
        setIsDeleteConfirmOpen(false);
      } else {
        toast.error("No se pudo eliminar el skill.");
      }
    } catch (err) {
      console.error("Error deleting skill:", err);
      toast.error("Error al intentar eliminar el skill.");
    } finally {
      setIsDeleting(false);
      setSkillToDelete(null);
    }
  };

  const filteredSkills = skills.filter(skill => {
    const matchesSearch = skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      skill.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = activeFilter === "all" || skill.agent_type === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="flex-1 flex flex-col bg-[#f8fafc]">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 p-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-purple-600" />
              </div>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                Centro de Skills
              </h1>
            </div>
            <p className="text-slate-500 text-lg">
              Gestiona y crea bibliotecas de conocimiento para tus agentes legales.
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => navigate("/dashboard/skills/generate")}
              className="border-2 border-purple-100 text-purple-600 font-bold py-6 px-8 rounded-2xl transition-all hover:bg-purple-50 hover:border-purple-200"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Generar con IA (Beta)
            </Button>
            
            <Button 
              onClick={() => navigate("/dashboard/skills/playbook-creation")}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-6 px-8 rounded-2xl shadow-lg shadow-purple-200 transition-all hover:scale-105"
            >
              <Plus className="mr-2 h-5 w-5" />
              Crear Playbook
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto w-full p-8 space-y-8">
        
        {/* Education Section for New Users */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-[2.5rem] p-8 text-white shadow-xl shadow-indigo-200 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-400/20 rounded-full -ml-10 -mb-10 blur-2xl" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-none px-4 py-1.5 text-sm font-bold backdrop-blur-md">
                <HelpCircle className="w-4 h-4 mr-2 inline" /> Guía Rápida
              </Badge>
              <h2 className="text-4xl font-black leading-tight">¿Qué es un Playbook Legal?</h2>
              <p className="text-indigo-100 text-lg leading-relaxed">
                Un Playbook es tu <strong>guía maestra de negociación</strong>. Contiene las reglas, cláusulas preferidas y límites que tu empresa acepta en contratos. 
                Nuestra IA convierte estos documentos estáticos en <strong>Habilidades (Skills)</strong> interactivas.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="font-semibold">Estandariza criterios</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="font-semibold">Reduce riesgos</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="font-semibold">Auditoría automática</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-3xl p-6 border border-white/10 backdrop-blur-md">
              <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" /> Cómo funciona
              </h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <p className="text-sm text-indigo-50"><strong>Sube tu guía:</strong> PDF o Word con tus políticas de contratación.</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <p className="text-sm text-indigo-50"><strong>IA Extractora:</strong> El sistema identifica automáticamente temas como Responsabilidad, Terminación, etc.</p>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <p className="text-sm text-indigo-50"><strong>Usa el Skill:</strong> Cuando revises un contrato, selecciona este skill para auditar el cumplimiento.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats / Featured */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-slate-900 font-bold text-xl mb-1">{skills.length}</h3>
            <p className="text-slate-500 text-sm">Skills Totales</p>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="text-slate-900 font-bold text-xl mb-1">Activos</h3>
            <p className="text-slate-500 text-sm">Skills Listos para Uso</p>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center mb-4">
              <Zap className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-slate-900 font-bold text-xl mb-1">Optimizado</h3>
            <p className="text-slate-500 text-sm">Mejora continua por IA</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-end">
            <div className="flex gap-2 bg-white p-1.5 rounded-2xl border border-gray-100 shadow-sm">
              {[
                { id: "all", label: "Todos", icon: Layers },
                { id: "contract_review", label: "Review", icon: Shield },
                { id: "legal_research", label: "Research", icon: Search },
                { id: "document_drafting", label: "Drafting", icon: FileText },
                { id: "chatbot", label: "Chatbot", icon: MessageSquare },
              ].map((filter) => (
                <Button
                  key={filter.id}
                  variant={activeFilter === filter.id ? "default" : "ghost"}
                  onClick={() => setActiveFilter(filter.id)}
                  className={cn(
                    "rounded-xl px-6 font-bold transition-all",
                    activeFilter === filter.id 
                      ? "bg-purple-600 hover:bg-purple-700 shadow-md shadow-purple-100" 
                      : "text-slate-500 hover:text-purple-600 hover:bg-purple-50"
                  )}
                >
                  <filter.icon className="mr-2 h-4 w-4" />
                  {filter.label}
                </Button>
              ))}
            </div>

            <div className="relative flex-1 max-w-md w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input 
                placeholder="Buscar habilidades..." 
                className="pl-12 py-6 bg-white border-gray-100 rounded-2xl text-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-12 w-12 text-purple-500 animate-spin mb-4" />
              <p className="text-slate-500 font-medium">Cargando habilidades...</p>
            </div>
          ) : (
            <>
              {filteredSkills.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredSkills.map((skill) => {
                    const AGENT_METADATA: Record<string, { icon: any, label: string, color: string, bg: string }> = {
                      contract_review: { icon: Shield, label: "Contract Review", color: "text-indigo-600", bg: "bg-indigo-50" },
                      legal_research: { icon: Search, label: "Legal Research", color: "text-amber-600", bg: "bg-amber-50" },
                      document_drafting: { icon: FileText, label: "Document Drafting", color: "text-purple-600", bg: "bg-purple-50" },
                      chatbot: { icon: MessageSquare, label: "Chatbot Agent", color: "text-emerald-600", bg: "bg-emerald-50" }
                    };
                    
                    const agentMeta = AGENT_METADATA[skill.agent_type || ""] || { 
                      icon: Layers, 
                      label: "Playbook", 
                      color: "text-slate-600", 
                      bg: "bg-slate-50" 
                    };

                    return (
                      <div 
                        key={skill.id} 
                        className="group bg-white rounded-3xl border border-gray-100 p-6 shadow-sm hover:shadow-xl hover:border-purple-100 transition-all duration-300 flex flex-col"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center transition-colors", agentMeta.bg)}>
                            <agentMeta.icon className={cn("h-6 w-6", agentMeta.color)} />
                          </div>
                          <Badge variant="secondary" className={cn("border-none px-3 py-1", agentMeta.bg, agentMeta.color)}>
                            {agentMeta.label}
                          </Badge>
                        </div>
                      
                        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-purple-700 transition-colors">
                          {skill.name}
                        </h3>
                        
                        <p className="text-slate-500 text-sm line-clamp-3 mb-6 flex-1">
                          {skill.description || "Sin descripción disponible."}
                        </p>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => {
                                setSkillToDelete(skill);
                                setIsDeleteConfirmOpen(true);
                              }}
                              className="h-9 w-9 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => navigate(`/dashboard/skills/edit/${skill.id}`)}
                              className="h-9 w-9 rounded-xl text-slate-400 hover:text-purple-600 hover:bg-purple-50"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <Button 
                            variant="ghost" 
                            onClick={() => handleViewDetails(skill)}
                            className="text-purple-600 font-bold hover:text-purple-700 hover:bg-purple-50 rounded-xl px-4"
                          >
                            Ver Detalles
                            <ChevronRight className="ml-1 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-dashed border-gray-300 p-20 text-center">
                  <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="h-10 w-10 text-slate-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">No se encontraron skills</h3>
                  <p className="text-slate-500 max-w-md mx-auto mb-8">
                    {searchQuery ? "No hay habilidades que coincidan con tu búsqueda." : "Aún no has creado ningún playbook. Comienza creando uno para potenciar tus análisis."}
                  </p>
                  {!searchQuery && (
                    <Button 
                      onClick={() => navigate("/dashboard/skills/playbook-creation")}
                      className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-6 px-10 rounded-2xl transition-all"
                    >
                      <Plus className="mr-2 h-5 w-5" />
                      Crear mi primer Playbook
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Skill Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-[2rem] border-none shadow-2xl p-0">
          {isLoadingDetail ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-12 w-12 text-purple-500 animate-spin mb-4" />
              <p className="text-slate-500">Cargando detalles del skill...</p>
            </div>
          ) : selectedSkill && (
            <div className="flex flex-col">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-8 text-white">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Layers className="h-8 w-8 text-white" />
                  </div>
                  <Badge className="bg-white/20 text-white border-none uppercase tracking-widest text-[10px]">
                    Biblioteca de Conocimiento
                  </Badge>
                </div>
                <h2 className="text-3xl font-black mb-2">{selectedSkill.name}</h2>
                <p className="text-purple-100 max-w-2xl">{selectedSkill.description}</p>
              </div>

              {/* Modal Body */}
              <div className="p-8 space-y-8 bg-white">
                <div>
                  <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Temas y Reglas Extraídas
                  </h3>
                  
                  <div className="space-y-4">
                    {selectedSkill.topics && selectedSkill.topics.length > 0 ? (
                      selectedSkill.topics.map((topic) => (
                        <div key={topic.id} className="border border-slate-100 rounded-2xl p-6 hover:border-purple-100 transition-colors bg-slate-50/50">
                          <h4 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500" />
                            {topic.name}
                          </h4>
                          <p className="text-slate-500 text-sm mb-4 italic">{topic.description}</p>
                          
                          <div className="space-y-3">
                            {topic.rules.map((rule, idx) => (
                              <div key={idx} className="flex gap-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                                <div className="w-6 h-6 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Scale className="h-3 w-3 text-emerald-600" />
                                </div>
                                <div className="space-y-1">
                                  <p className="text-slate-700 text-sm font-medium">{rule.text}</p>
                                  {rule.source_reference && (
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                      <Info className="w-3 h-3" /> Ref: {rule.source_reference}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">Este skill no tiene temas estructurados aún.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <div className="flex items-center justify-between text-xs text-slate-400 font-medium">
                    <p>Creado: {selectedSkill.created_at ? new Date(selectedSkill.created_at).toLocaleDateString() : 'N/A'}</p>
                    <p>Origen: {selectedSkill.source_filename || 'Carga manual'}</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="p-6 bg-slate-50 border-t border-slate-100">
                <Button variant="outline" onClick={() => setIsDetailOpen(false)} className="rounded-xl px-8 font-bold border-slate-200">
                  Cerrar
                </Button>
                <Button 
                  onClick={() => navigate(`/dashboard/skills/edit/${selectedSkill.id}`)}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl px-8 ml-2"
                >
                  Editar Skill
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[425px] rounded-[2rem] border-none shadow-2xl p-8">
          <DialogHeader>
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-4">
              <Trash2 className="h-8 w-8 text-red-600" />
            </div>
            <DialogTitle className="text-2xl font-black text-slate-900">¿Eliminar este Skill?</DialogTitle>
            <DialogDescription className="text-slate-500 text-lg py-2">
              Esta acción es permanente. Se eliminará el playbook <strong>{skillToDelete?.name}</strong> y todas sus reglas asociadas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex gap-3 sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="flex-1 rounded-xl py-6 font-bold border-slate-200"
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteSkill}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl py-6 shadow-lg shadow-red-100"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                "Sí, Eliminar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
